import React from 'react';
import { Form, Button, DatePicker, TimePicker, Select } from '@crisellevaldez/speculo-ui';
import { getMinTime, getMaxTime, getMinDate } from '../../../../utils/dateTime';
import { TIMEZONE_OPTIONS } from '../../../../constants/timezones';

interface FlowSchedulingProps {
  selectedDate: Date | null;
  selectedTime: string | null;
  scheduledError?: string;
  timezone: string;
  onDateChange: (date: Date | null) => void;
  onTimeChange: (time: string | null) => void;
  onTimezoneChange?: (timezone: string) => void;
  onCountryChange?: (country: string) => void;
  onAdvancedSettingsClick: () => void;
  onValidateScheduled?: (date: Date | null, time: string | null, timezoneOverride?: string) => void;
  disabled?: boolean;
  editableFields: {
    scheduledFor: boolean;
  };
}

export const FlowScheduling: React.FC<FlowSchedulingProps> = ({
  selectedDate,
  selectedTime,
  scheduledError,
  timezone,
  onDateChange,
  onTimeChange,
  onTimezoneChange,
  onCountryChange,
  onAdvancedSettingsClick,
  onValidateScheduled,
  disabled,
  editableFields,
}) => {
  return (
    <Form.Control>
      <div className="flex justify-between items-center">
        <Form.Label>Scheduled For:</Form.Label>
        <Button
          variant="outline"
          size="sm"
          className="py-2 mb-2"
          onClick={onAdvancedSettingsClick}
          disabled={disabled}
        >
          Advanced Settings
        </Button>
      </div>
      {/* Timezone Selection */}
      <div className="mt-3">
        <Form.Label className="text-sm mb-2">Timezone</Form.Label>
        <Select
          placeholder="Select timezone"
          value={timezone}
          onChange={(value) => {
            const newTimezone = value as string;
            if (onTimezoneChange) {
              onTimezoneChange(newTimezone);
            }
            // Auto-set country based on timezone
            if (onCountryChange) {
              if (newTimezone.includes('Canada') || newTimezone.startsWith('Canada/')) {
                onCountryChange('CA');
              } else {
                onCountryChange('US');
              }
            }
            // Re-validate with new timezone immediately by passing it directly
            if (
              editableFields.scheduledFor &&
              onValidateScheduled &&
              selectedDate &&
              selectedTime
            ) {
              onValidateScheduled(selectedDate, selectedTime, newTimezone);
            }
          }}
          options={TIMEZONE_OPTIONS}
          searchable
          disabled={disabled || !editableFields.scheduledFor}
          helperText="Select the timezone for scheduling this flow."
        />
      </div>
      <div className="mt-6 space-y-2">
        <div className="flex gap-2">
          <div className="flex-1">
            <DatePicker
              placeholder="Select start date"
              value={selectedDate}
              onChange={(date) => {
                onDateChange(date);
                if (editableFields.scheduledFor && onValidateScheduled) {
                  onValidateScheduled(date, selectedTime);
                }
              }}
              minDate={getMinDate(timezone)}
              error={scheduledError}
              disabled={disabled || !editableFields.scheduledFor}
            />
          </div>
          <div className="w-64">
            <TimePicker
              placeholder="Select time"
              value={selectedTime}
              onChange={(time) => {
                if (!selectedDate) return;
                const minTime = getMinTime(selectedDate, timezone || 'UTC');
                if (time) {
                  onTimeChange(time);
                  if (editableFields.scheduledFor && onValidateScheduled) {
                    onValidateScheduled(selectedDate, time);
                  }
                }
              }}
              minTime={
                selectedDate ? getMinTime(selectedDate, timezone || 'UTC') || undefined : undefined
              }
              maxTime={getMaxTime(timezone || 'UTC')}
              step={60}
              error={scheduledError}
              disabled={disabled || !selectedDate || !editableFields.scheduledFor}
            />
          </div>
        </div>
      </div>
    </Form.Control>
  );
};
